/**
 * Search Logger Service
 * Logs user search activities and interactions for analytics
 * Based on add_search_log from lot-wizard user_funcs.py
 */

import sessionLogger from './sessionLogger';

class SearchLogger {
  constructor() {
    this.baseUrl = this.getApiBaseUrl();
    this.pendingLogs = [];
    this.isProcessing = false;
  }

  // Get API base URL based on environment
  getApiBaseUrl() {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalhost) {
      // Development - direct connection to remote backend
      return 'http://64.225.20.106:8008/api';
    } else {
      // Production - nginx proxy
      return '/backend';
    }
  }

  // Log search activity
  async logActivity({
    functionPage,
    activityType = 'search',
    searchParameters = {},
    filtersApplied = {},
    resultsCount = 0,
    responseTimeMs = 0,
    additionalData = {}
  }, authToken = null) {
    try {
      if (!functionPage) {
        console.warn('Search logger: functionPage is required');
        return { status: 'error', error: 'functionPage is required' };
      }

      // Get session information
      const sessionInfo = sessionLogger.getSessionInfo();
      await sessionLogger.initializeIP();

      // Prepare search log data
      const searchLogData = {
        function_page: functionPage,
        activity_type: activityType,
        search_parameters: searchParameters,
        filters_applied: filtersApplied,
        results_count: resultsCount,
        response_time_ms: responseTimeMs,
        session_id: sessionInfo.sessionId,
        ip_address: sessionInfo.ipAddress,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        platform: 'react_web',
        timestamp: new Date().toISOString(),
        ...additionalData
      };

      // Prepare headers
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add authorization header if token provided
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      console.log(`📊 Logging ${activityType} activity:`, {
        functionPage,
        activityType,
        resultsCount,
        responseTimeMs
      });

      // Send to backend API
      const response = await fetch(`${this.baseUrl}/log-search`, {
        method: 'POST',
        headers,
        body: JSON.stringify(searchLogData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Activity logged successfully: ${functionPage} - ${activityType}`);
        
        return {
          status: 'success',
          functionPage,
          activityType,
          timestamp: result.timestamp
        };
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.warn(`⚠️ Activity logging failed:`, response.status, errorData);
        
        return {
          status: 'error',
          error: errorData.error || `HTTP ${response.status}`
        };
      }

    } catch (error) {
      console.error('❌ Error logging search activity:', error);
      return {
        status: 'error',
        error: error.message
      };
    }
  }

  // Convenience methods for different activity types

  // Log load search
  async logLoadSearch(searchFilters = {}, resultsCount = 0, responseTimeMs = 0, authToken = null) {
    return this.logActivity({
      functionPage: 'search_loads',
      activityType: 'search',
      searchParameters: {
        origin: searchFilters.origin,
        pickupDate: searchFilters.pickupDate,
        equipment: searchFilters.equipment,
        originRange: searchFilters.originRange
      },
      filtersApplied: {
        minPayment: searchFilters.minPayment,
        minRatePerMile: searchFilters.minRatePerMile,
        minMiles: searchFilters.minMiles,
        equipmentTypes: searchFilters.equipmentTypes,
        destinationStates: searchFilters.destinationStates,
        loadTypes: searchFilters.loadTypes,
        haulTypes: searchFilters.haulTypes
      },
      resultsCount,
      responseTimeMs
    }, authToken);
  }

  // Log broker metrics search
  async logBrokerMetrics(filters = {}, resultsCount = 0, responseTimeMs = 0, authToken = null) {
    return this.logActivity({
      functionPage: 'broker_metrics',
      activityType: 'search',
      searchParameters: filters,
      resultsCount,
      responseTimeMs
    }, authToken);
  }

  // Log rate insights search
  async logRateInsights(filters = {}, resultsCount = 0, responseTimeMs = 0, authToken = null) {
    return this.logActivity({
      functionPage: 'rate_insights',
      activityType: 'search',
      searchParameters: filters,
      resultsCount,
      responseTimeMs
    }, authToken);
  }

  // Log lane pricing search
  async logLanePricing(searchData = {}, resultsCount = 0, responseTimeMs = 0, authToken = null) {
    return this.logActivity({
      functionPage: 'lane_pricing',
      activityType: 'search',
      searchParameters: {
        originCity: searchData.originCity,
        originState: searchData.originState,
        destinationCity: searchData.destinationCity,
        destinationState: searchData.destinationState,
        equipmentType: searchData.equipmentType,
        radiusMiles: searchData.radiusMiles
      },
      resultsCount,
      responseTimeMs
    }, authToken);
  }

  // Log map interactions
  async logMapActivity(activityType = 'view', mapData = {}, authToken = null) {
    return this.logActivity({
      functionPage: 'interactive_map',
      activityType,
      searchParameters: mapData,
      additionalData: {
        mapType: mapData.mapType,
        zoomLevel: mapData.zoomLevel,
        centerCoordinates: mapData.centerCoordinates
      }
    }, authToken);
  }

  // Log city click on bubble map
  async logCityClick(cityData = {}, authToken = null) {
    return this.logActivity({
      functionPage: 'search_loads',
      activityType: 'city_click',
      searchParameters: {
        cityName: cityData.cityName,
        cityState: cityData.cityState,
        loadCount: cityData.loadCount
      },
      additionalData: {
        bubbleSize: cityData.bubbleSize,
        avgPayment: cityData.avgPayment,
        avgRate: cityData.avgRate
      }
    }, authToken);
  }

  // Log route generation
  async logRouteGeneration(routeData = {}, authToken = null) {
    return this.logActivity({
      functionPage: 'search_loads',
      activityType: 'route_generation',
      searchParameters: {
        origin: routeData.origin,
        destination: routeData.destination,
        distance: routeData.distance,
        duration: routeData.duration
      }
    }, authToken);
  }

  // Log filter changes
  async logFilterChange(functionPage, filterName, oldValue, newValue, authToken = null) {
    return this.logActivity({
      functionPage,
      activityType: 'filter_change',
      searchParameters: {
        filterName,
        oldValue,
        newValue
      }
    }, authToken);
  }

  // Log table sorting
  async logTableSort(functionPage, sortColumn, sortDirection, authToken = null) {
    return this.logActivity({
      functionPage,
      activityType: 'table_sort',
      searchParameters: {
        sortColumn,
        sortDirection
      }
    }, authToken);
  }

  // Log data export
  async logDataExport(functionPage, exportType, recordCount, authToken = null) {
    return this.logActivity({
      functionPage,
      activityType: 'data_export',
      searchParameters: {
        exportType,
        recordCount
      }
    }, authToken);
  }

  // Batch logging for multiple activities (with rate limiting)
  async batchLog(activities, authToken = null) {
    const results = [];
    for (const activity of activities) {
      const result = await this.logActivity(activity, authToken);
      results.push(result);
      // Small delay to prevent overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return results;
  }
}

// Create singleton instance
const searchLogger = new SearchLogger();

export { SearchLogger, searchLogger };
export default searchLogger;